import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getAllProducts, getCategories, searchProducts, getProductsByCategory } from '../services/productsService';
import { ShoppingCart, MagnifyingGlass } from 'phosphor-react';

// Helper function to check if a product is new (released within 30 days)
const isNewProduct = (releaseDate) => {
  if (!releaseDate) return false;
  const release = new Date(releaseDate);
  const now = new Date();
  const diffTime = Math.abs(now - release);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30;
};

function Shop() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToCart } = useCart();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsData, categoriesData] = await Promise.all([
          getAllProducts(),
          getCategories()
        ]);

        setProducts(productsData);
        setFilteredProducts(productsData);
        setCategories(categoriesData);
      } catch (err) {
        setError(t('common.error'));
        console.error('Error loading shop data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  // Handle category filter toggle
  const handleCategoryToggle = async (categoryName) => {
    setSearchTerm(''); // Clear search when filtering by category
    
    let newSelectedCategories;
    if (categoryName === 'All') {
      newSelectedCategories = [];
    } else {
      newSelectedCategories = selectedCategories.includes(categoryName)
        ? selectedCategories.filter(cat => cat !== categoryName)
        : [...selectedCategories, categoryName];
    }
    
    setSelectedCategories(newSelectedCategories);
    
    try {
      if (newSelectedCategories.length === 0) {
        // Show all products
        const data = await getAllProducts();
        setFilteredProducts(data);
      } else {
        // Filter products by selected categories
        const filteredData = products.filter(product => 
          newSelectedCategories.includes(product.category?.name)
        );
        setFilteredProducts(filteredData);
      }
    } catch (err) {
      setError(t('common.error'));
      console.error('Error filtering products:', err);
    }
  };

  // Handle search
  const handleSearch = async (term) => {
    setSearchTerm(term);
    
    if (!term.trim()) {
      // If search is empty, show all products or filtered by selected categories
      if (selectedCategories.length === 0) {
        setFilteredProducts(products);
      } else {
        const filteredData = products.filter(product => 
          selectedCategories.includes(product.category?.name)
        );
        setFilteredProducts(filteredData);
      }
      return;
    }

    try {
      const data = await searchProducts(term);
      setFilteredProducts(data);
      setSelectedCategories([]); // Reset category filter when searching
    } catch (err) {
      setError(t('common.error'));
      console.error('Error searching products:', err);
    }
  };

  const handleAddToCart = (product) => {
    // Transform Supabase product to match cart context format
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      category_id: product.category_id,
      image_url: product.image_url,
      use_standard_pricing: product.use_standard_pricing,
      stock_quantity: product.stock_quantity
    };
    addToCart(cartItem);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ambre-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-ambre-600 text-white px-6 py-2 rounded-lg hover:bg-ambre-700 transition"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      
      {/* Shop Header */}
      <section className="py-16 px-8 bg-white">
        <div className="max-w-6xl mx-auto text-left">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{t('shop.title')}</h1>
          <span className="block text-md text-gray-500 max-w-full sm:max-w-lg md:max-w-2xl lg:max-w-3xl">Alle kaartjes worden gedrukt op kwaliteitsvol papier met een subtiele lijnstructuur. Vooraan vind je een kleurrijke knipseltekst, de achterkant is blanco zodat je er een persoonlijke boodschap kan schrijven. 
            <br />
            <ul className="list-disc list-inside my-4">
              <li><span className="font-bold">Per kaartje:</span> €3,00
              </li>
              <li><span className="font-bold">Vanaf 4 kaartjes*</span>: €2,75
              </li>
              <li><span className="font-bold">Vanaf 8 kaartjes*</span>: €2,50
              </li>
            </ul>            
            Bij elke kaart hoort een gerecycleerde envelop, die inbegrepen is in de prijs. </span>
            <span className="text-sm text-gray-500 mt-4 block">*Pick & mix: de korting geldt voor alle combinaties van kaartjes.</span>
        </div>
      </section>
      
      {/* Filters and Search */}
      <section className="py-8 px-8 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-6">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
              />
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                key="all"
                onClick={() => handleCategoryToggle('All')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  selectedCategories.length === 0
                    ? 'bg-ambre-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('shop.filter.all')}
              </button>
              {categories
                .filter(category => category.name && category.name.trim() !== '') // Remove empty categories
                .map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryToggle(category.name)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    selectedCategories.includes(category.name)
                      ? 'bg-ambre-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            {t('shop.showing', { count: filteredProducts.length })}
          </div>
        </div>
      </section>
      
      {/* Products Grid */}
      <section className="py-16 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={64} className="text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('shop.noProducts')}</h3>
              <p className="text-gray-500">{t('shop.noProductsSubtitle')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8">
              {filteredProducts.map((product) => (
                <div key={product.id} className="group">
                  <Link to={`/product/${product.id}`} className="block">
                    <div className="w-full bg-gray-100 overflow-hidden mb-4 group-hover:opacity-90 transition-opacity shadow-lg relative" style={{ aspectRatio: '105/148' }}>
                      {isNewProduct(product.release_date) && (
                        <div className="absolute top-2 left-2 z-10">
                          <span className="bg-green-600 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                            {t('product.new')}
                          </span>
                        </div>
                      )}
                      {product.stock_quantity === 0 && (
                        <div className="absolute top-2 right-2 z-10">
                          <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                            {t('product.temporarilyOutOfStock')}
                          </span>
                        </div>
                      )}
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover object-center"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full bg-gradient-to-br from-ambre-100 to-ambre-200 flex items-center justify-center">
                        <span className="text-lg font-bold text-ambre-600">{product.category?.name || 'No Category'}</span>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 flex-1">
                          {product.name}
                        </h3>
                        <span className="ml-3 px-2 py-1 text-xs font-medium bg-ambre-100 text-ambre-800 rounded-full">
                          {product.category?.name || 'No Category'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-lg font-bold text-ambre-600">€{product.price.toFixed(2)}</p>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddToCart(product);
                          }}
                          disabled={product.stock_quantity === 0}
                          className="bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed text-black p-2 rounded-lg transition-colors duration-200 flex items-center justify-center ml-3"
                          title={product.stock_quantity === 0 ? t('product.temporarilyOutOfStock') : t('cart.addToCart')}
                        >
                          <ShoppingCart size={16} className="text-black" />
                        </button>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default Shop; 